import { useState, useEffect } from "react";
import { IcButton, IcSelect, IcTextField, IcStatusTag, IcAlert } from "@ukic/react";

import {
  api,
  FREQ_UNITS,
  FREQ_UNIT_HZ,
  hzToValueUnit,
  DECODER_FORMATS,
  protocolForFormat,
  datatypeForSdr,
  projectedBytes,
  DISK_HEADROOM_BYTES,
  type SdrSlot,
  type SdrType,
  type Preset,
} from "../api";
import { humanHz, humanBytes } from "../format";
import { useToast } from "../toast";

type Action = "live" | "capture" | "decode";

const TASK_STATUS: Record<string, "neutral" | "success" | "warning"> = {
  idle: "neutral",
  live: "success",
  capture: "warning",
  decode: "warning",
};

export function SdrCard({
  deviceId,
  slot,
  deviceActive,
  diskFree,
  liveElsewhere,
  decodersReady,
  decoderSdrs,
  onChanged,
  onOpenLive,
}: {
  deviceId: string;
  slot: SdrSlot;
  deviceActive: boolean;
  diskFree: number | null;
  liveElsewhere: boolean;
  decodersReady?: Record<string, boolean>;
  decoderSdrs?: Record<string, string[]>;
  onChanged: () => void;
  onOpenLive: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<Action>("decode");

  // capture form
  const [capFreq, setCapFreq] = useState("433.92");
  const [capUnit, setCapUnit] = useState("MHz");
  const [bw, setBw] = useState("2.4");            // bandwidth value (= sample rate)
  const [bwUnit, setBwUnit] = useState("MHz");    // bandwidth unit
  const [duration, setDuration] = useState("120");
  const [capGain, setCapGain] = useState("");     // tuner gain dB; blank = auto/AGC
  const [bbFilter, setBbFilter] = useState("");   // HackRF baseband filter (MHz); blank = auto
  // decode form
  const [decFormat, setDecFormat] = useState("rtl_433");
  const [decFreq, setDecFreq] = useState("433.92");
  const [decUnit, setDecUnit] = useState("MHz");
  const [decChannels, setDecChannels] = useState("147.2375, 147.250, 147.300"); // MHz, multi-channel
  // Saved channel presets for this format's protocol (prefill freq / cluster).
  const [presets, setPresets] = useState<Preset[]>([]);

  const sdrType = (slot.type as SdrType) ?? "rtlsdr";
  const datatype = datatypeForSdr(sdrType);

  const capHz = Math.round((Number(capFreq) || 0) * (FREQ_UNIT_HZ[capUnit] ?? 1));
  const decHz = Math.round((Number(decFreq) || 0) * (FREQ_UNIT_HZ[decUnit] ?? 1));
  // Sample rate IS the captured bandwidth (complex IQ → span ≈ sample rate).
  const rateNum = Math.round((Number(bw) || 0) * (FREQ_UNIT_HZ[bwUnit] ?? 1));
  const capGainNum = capGain.trim() === "" ? undefined : Number(capGain);
  const bbFilterHz = bbFilter.trim() === "" ? undefined : Math.round(Number(bbFilter) * 1e6);
  const isHackrf = sdrType === "hackrf";
  const durNum = Number(duration) || 0;
  const projected = projectedBytes(rateNum, durNum, datatype);
  const allowed = diskFree != null ? Math.max(0, diskFree - DISK_HEADROOM_BYTES) : null;
  const overDisk = allowed != null && projected > allowed;
  const decFmtInfo = DECODER_FORMATS.find((f) => f.value === decFormat);
  const isMulti = !!decFmtInfo?.multi;
  // Per-device readiness from agent status: true/false if known, undefined while
  // status hasn't loaded. Only flag/disable when we KNOW the binary is missing;
  // before status loads, fall back to the static needsBuild hint (non-blocking).
  const decReady = decodersReady?.[decFormat];
  const decMissing = decReady === false;
  const showBuildHint = decMissing || (decReady === undefined && !!decFmtInfo?.needsBuild);
  // SDR compatibility: a decoder may not run on this SDR type (e.g. RTL-only on a
  // HackRF). Grey those out in the menu and block Start. decoderSdrs[fmt] lists the
  // supported types; undefined (status not loaded) = assume compatible.
  const fmtOk = (fmt: string) => {
    const sup = decoderSdrs?.[fmt];
    return !sup || sup.includes(sdrType);
  };
  const decIncompatible = !fmtOk(decFormat);
  // multi-channel: parse the comma-separated MHz list -> Hz integers
  const decChannelHz = decChannels
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Math.round(Number(s) * 1e6))
    .filter((n) => Number.isFinite(n) && n > 0);

  // Load presets matching the selected decoder's protocol (gsm/pocsag/…).
  useEffect(() => {
    let on = true;
    api
      .listPresets(protocolForFormat(decFormat))
      .then((p) => on && setPresets(p))
      .catch(() => on && setPresets([]));
    return () => {
      on = false;
    };
  }, [decFormat]);

  // Apply a saved preset to the decode form: a cluster fills channels (-multi),
  // a single freq fills the frequency field. Cross-fill if only the other exists.
  const applyPreset = (id: string) => {
    const p = presets.find((x) => String(x.id) === id);
    if (!p) return;
    if (isMulti) {
      const ch = p.channels && p.channels.length ? p.channels : p.freq ? [p.freq] : [];
      if (ch.length) setDecChannels(ch.map((c) => String(c / 1e6)).join(", "));
    } else {
      const hz = p.freq ?? (p.channels && p.channels[0]);
      if (hz) {
        const vu = hzToValueUnit(hz);
        setDecFreq(vu.value);
        setDecUnit(vu.unit);
      }
    }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error("Action failed", e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const stop = () =>
    act(() => api.setSdrTask(deviceId, slot.id, { task: "idle" }), `SDR ${slot.index} stopped`);

  const start = () => {
    if (action === "live") {
      return act(
        () => api.setSdrTask(deviceId, slot.id, { task: "live" }),
        `SDR ${slot.index} → Live`,
      );
    }
    if (action === "capture") {
      return act(
        () =>
          api.setSdrTask(deviceId, slot.id, {
            task: "capture",
            freq: capHz,
            rate: rateNum,
            duration_s: durNum,
            ...(capGainNum !== undefined ? { gain: capGainNum } : {}),
            ...(bbFilterHz !== undefined ? { bb_filter: bbFilterHz } : {}),
          }),
        `SDR ${slot.index} → Capture`,
      );
    }
    return act(
      () =>
        api.setSdrTask(deviceId, slot.id, {
          task: "decode",
          format: decFormat,
          ...(isMulti ? { channels: decChannelHz } : { freq: decHz }),
        }),
      `SDR ${slot.index} → Decode (${decFormat})`,
    );
  };

  const d = slot.detail ?? {};

  return (
    <div className="hl-panel hl-stack hl-sdr-card">
      <div className="hl-row hl-sdr-head">
        <strong>SDR {slot.index}</strong>
        <span className="hl-muted">
          {slot.type} · port {slot.port} · {slot.label}
        </span>
        <IcStatusTag label={slot.task} status={TASK_STATUS[slot.task] ?? "neutral"} />
      </div>

      {slot.task === "live" && (
        <div className="hl-row">
          <span className="hl-muted">Live{d.freq ? ` · ${humanHz(d.freq)}` : ""}</span>
          <IcButton size="small" onClick={onOpenLive}>Open Live</IcButton>
          <IcButton size="small" variant="secondary" onClick={stop} loading={busy}>Stop</IcButton>
        </div>
      )}

      {slot.task === "capture" && (
        <div className="hl-row">
          <span className="hl-muted">
            Capturing {d.freq ? humanHz(d.freq) : ""} {d.duration_s ? `· ${d.duration_s}s` : ""}
          </span>
          <IcButton size="small" variant="secondary" onClick={stop} loading={busy}>Stop</IcButton>
        </div>
      )}

      {slot.task === "decode" && (
        <div className="hl-row">
          <span className="hl-muted">
            Decoding <code>{d.format}</code>
            {d.freq ? ` · ${humanHz(d.freq)}` : ""} · {d.count ?? 0} records
            {d.last_label ? ` · last: ${d.last_label}` : ""}
          </span>
          <IcButton size="small" variant="tertiary" href="/signals">
            Signals
          </IcButton>
          <IcButton size="small" variant="secondary" onClick={stop} loading={busy}>Stop</IcButton>
        </div>
      )}

      {slot.task === "idle" && (
        <div className="hl-stack">
          <div className="hl-row">
            <IcSelect
              label="Task"
              hideLabel
              value={action}
              options={[
                { label: "Live", value: "live" },
                { label: "Capture", value: "capture" },
                { label: "Decode", value: "decode" },
              ]}
              onIcChange={(e) => setAction((e as CustomEvent<{ value: string }>).detail.value as Action)}
            />

            {action === "capture" && (
              <>
                <IcTextField label="Freq" hideLabel placeholder="freq" type="number" value={capFreq}
                  onIcInput={(e) => setCapFreq((e as CustomEvent<{ value: string }>).detail.value)} />
                <IcSelect label="Unit" hideLabel value={capUnit} options={FREQ_UNITS}
                  onIcChange={(e) => setCapUnit((e as CustomEvent<{ value: string }>).detail.value)} />
                <IcTextField label="Bandwidth" hideLabel placeholder="bandwidth" type="number" value={bw}
                  onIcInput={(e) => setBw((e as CustomEvent<{ value: string }>).detail.value)} />
                <IcSelect label="BW unit" hideLabel value={bwUnit} options={FREQ_UNITS}
                  onIcChange={(e) => setBwUnit((e as CustomEvent<{ value: string }>).detail.value)} />
                <IcTextField label="Dur" hideLabel placeholder="sec" type="number" value={duration}
                  onIcInput={(e) => setDuration((e as CustomEvent<{ value: string }>).detail.value)} />
                <IcTextField label="Gain" hideLabel placeholder={isHackrf ? "gain dB (blank=def)" : "gain dB (blank=AGC)"}
                  type="number" value={capGain}
                  onIcInput={(e) => setCapGain((e as CustomEvent<{ value: string }>).detail.value)} />
                {isHackrf && (
                  <IcTextField label="BB filter MHz" hideLabel placeholder="BB filter MHz (blank=auto)"
                    type="number" value={bbFilter}
                    onIcInput={(e) => setBbFilter((e as CustomEvent<{ value: string }>).detail.value)} />
                )}
              </>
            )}

            {action === "decode" && (
              <>
                <IcSelect label="Decoder" hideLabel value={decFormat}
                  options={DECODER_FORMATS.map((f) => {
                    const ok = fmtOk(f.value);
                    return {
                      label: ok ? f.value : `${f.value} — not on ${sdrType}`,
                      value: f.value,
                      disabled: !ok,
                    };
                  })}
                  onIcChange={(e) => {
                    const v = (e as CustomEvent<{ value: string }>).detail.value;
                    setDecFormat(v);
                    const f = DECODER_FORMATS.find((x) => x.value === v);
                    if (f) {
                      const vu = hzToValueUnit(f.defaultHz);
                      setDecFreq(vu.value);
                      setDecUnit(vu.unit);
                      if (f.defaultChannels) setDecChannels(f.defaultChannels);
                    }
                  }} />
                {presets.length > 0 && (
                  <IcSelect label="Preset" hideLabel value=""
                    options={[
                      { label: `Load preset (${presets.length})`, value: "" },
                      ...presets.map((p) => ({ label: p.label, value: String(p.id) })),
                    ]}
                    onIcChange={(e) => applyPreset((e as CustomEvent<{ value: string }>).detail.value)} />
                )}
                {isMulti ? (
                  <IcTextField label="Channels (MHz)" hideLabel placeholder="147.2375, 147.250, 147.300"
                    value={decChannels}
                    onIcInput={(e) => setDecChannels((e as CustomEvent<{ value: string }>).detail.value)} />
                ) : (
                  <>
                    <IcTextField label="Freq" hideLabel placeholder="freq" type="number" value={decFreq}
                      onIcInput={(e) => setDecFreq((e as CustomEvent<{ value: string }>).detail.value)} />
                    <IcSelect label="Unit" hideLabel value={decUnit} options={FREQ_UNITS}
                      onIcChange={(e) => setDecUnit((e as CustomEvent<{ value: string }>).detail.value)} />
                  </>
                )}
              </>
            )}

            <IcButton
              size="small"
              onClick={start}
              loading={busy}
              disabled={
                !deviceActive ||
                (action === "live" && liveElsewhere) ||
                (action === "capture" && (overDisk || rateNum <= 0 || durNum <= 0 || capHz <= 0)) ||
                (action === "decode" &&
                  (decMissing || decIncompatible || (isMulti ? decChannelHz.length === 0 : decHz <= 0)))
              }
            >
              Start
            </IcButton>
          </div>

          {action === "capture" && (
            <span className="hl-muted">
              {humanHz(capHz)} · {humanHz(rateNum)} bandwidth · 8-bit IQ ({datatype}) → ~{humanBytes(projected)}
              {capGainNum !== undefined ? ` · gain ${capGainNum}dB` : " · gain auto"}
              {overDisk ? " · too large for free disk" : ""}
              {" — lower bandwidth/duration = smaller files (bit depth is fixed 8-bit)"}
            </span>
          )}
          {action === "decode" && (
            <span className="hl-muted">
              {isMulti
                ? `${decFormat} · ${decChannelHz.length} channel${decChannelHz.length === 1 ? "" : "s"}`
                : `${decFormat} @ ${humanHz(decHz)}`}
              {decIncompatible
                ? ` · not supported on ${sdrType}`
                : decMissing
                  ? " · not installed — run install-decoders.sh on the device"
                  : showBuildHint
                    ? " · may need install-decoders.sh on the device"
                    : ""}
            </span>
          )}
          {action === "live" && liveElsewhere && (
            <IcAlert variant="info" heading="Another SDR is Live"
              message="Only one SDR can be Live at a time. Stop the other first." />
          )}
        </div>
      )}
    </div>
  );
}
